import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Share2, Download, Copy } from "lucide-react";
import { toast } from "sonner";

const W = 1200;
const H = 630;

function colorFor(rating) {
  switch (rating) {
    case "Strong":
    case "Healthy":
      return "#2C5C43";
    case "Watchlist":
      return "#9c6b1d";
    case "Stressed":
    case "Critical":
      return "#A84632";
    default:
      return "#5C665D";
  }
}

function wrap(ctx, text, maxWidth) {
  const words = (text || "").split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export default function ShareScoreDialog({ report }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    if (!open || !report) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    const score = Math.round(report.health?.overall_score ?? 0);
    const rating = report.health?.rating || "—";
    const risk = report.health?.risk_level || "—";
    const letterGrade = report.health?.letter_grade || "—";
    const company = report.input?.company_name || "Your Business";
    const period = report.input?.period || "Latest period";
    const industry = report.benchmark?.label || report.input?.industry || "General";
    const strengths = (report.findings?.strengths || []).slice(0, 1);
    const risks = (report.findings?.risks || []).slice(0, 1);
    const ringColor = colorFor(rating);

    // Background
    ctx.fillStyle = "#F7F5F0";
    ctx.fillRect(0, 0, W, H);

    // Subtle texture noise
    ctx.globalAlpha = 0.04;
    for (let i = 0; i < 1500; i++) {
      ctx.fillStyle = "#1F382E";
      ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
    }
    ctx.globalAlpha = 1;

    // Card
    const cx = 60, cy = 60, cw = W - 120, ch = H - 120;
    ctx.fillStyle = "#FFFFFF";
    ctx.strokeStyle = "#E5E0D8";
    ctx.lineWidth = 2;
    roundRect(ctx, cx, cy, cw, ch, 24);
    ctx.fill();
    ctx.stroke();

    // Brand mark
    ctx.fillStyle = "#1F382E";
    roundRect(ctx, 100, 100, 56, 56, 12);
    ctx.fill();
    ctx.strokeStyle = "#D4C9B6";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(112, 140);
    ctx.lineTo(124, 128);
    ctx.lineTo(134, 134);
    ctx.lineTo(146, 116);
    ctx.stroke();
    ctx.fillStyle = "#C26D5C";
    ctx.beginPath();
    ctx.arc(146, 116, 4.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#1F382E";
    ctx.font = "500 32px 'Cormorant Garamond', serif";
    ctx.fillText("CFO Grade", 172, 140);

    // Eyebrow
    ctx.fillStyle = "#5C665D";
    ctx.font = "500 14px Manrope, sans-serif";
    ctx.fillText("CFO GRADE", 100, 220);

    // Company name
    ctx.fillStyle = "#1F382E";
    ctx.font = "600 56px 'Cormorant Garamond', serif";
    const nameLines = wrap(ctx, company, 700);
    nameLines.slice(0, 2).forEach((line, i) => {
      ctx.fillText(line, 100, 270 + i * 60);
    });

    ctx.fillStyle = "#5C665D";
    ctx.font = "400 18px Manrope, sans-serif";
    ctx.fillText(`${period}  ·  ${industry}`, 100, nameLines.length > 1 ? 390 : 330);

    // Score ring (right side)
    const ringX = W - 260;
    const ringY = H / 2;
    const radius = 130;

    ctx.strokeStyle = "#E5E0D8";
    ctx.lineWidth = 22;
    ctx.beginPath();
    ctx.arc(ringX, ringY, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = ringColor;
    ctx.lineWidth = 22;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.arc(
      ringX,
      ringY,
      radius,
      -Math.PI / 2,
      -Math.PI / 2 + (Math.max(0, Math.min(100, score)) / 100) * Math.PI * 2
    );
    ctx.stroke();
    ctx.lineCap = "butt";

    // Letter grade (hero) + score line
    ctx.fillStyle = "#C26D5C";
    ctx.font = "600 140px 'Cormorant Garamond', serif";
    ctx.textAlign = "center";
    ctx.fillText(letterGrade, ringX, ringY + 22);
    ctx.fillStyle = "#1F382E";
    ctx.font = "500 28px 'Cormorant Garamond', serif";
    ctx.fillText(`${score} / 100`, ringX, ringY + 64);
    ctx.textAlign = "left";

    // Rating badge below ring
    ctx.fillStyle = ringColor;
    const badgeText = `${rating} · ${risk} risk`;
    ctx.font = "500 18px Manrope, sans-serif";
    const tw = ctx.measureText(badgeText).width;
    roundRect(ctx, ringX - tw / 2 - 16, ringY + 80, tw + 32, 36, 18);
    ctx.fillStyle = ringColor + "22";
    ctx.fill();
    ctx.fillStyle = ringColor;
    ctx.textAlign = "center";
    ctx.fillText(badgeText, ringX, ringY + 104);
    ctx.textAlign = "left";

    // Highlights
    let y = nameLines.length > 1 ? 440 : 380;
    if (strengths.length) {
      ctx.fillStyle = "#2C5C43";
      ctx.font = "600 14px Manrope, sans-serif";
      ctx.fillText("STRENGTH", 100, y);
      ctx.fillStyle = "#1A1A1A";
      ctx.font = "400 18px Manrope, sans-serif";
      const lines = wrap(ctx, strengths[0], 640);
      lines.slice(0, 2).forEach((l, i) => ctx.fillText(l, 100, y + 28 + i * 24));
      y += 28 + Math.min(2, lines.length) * 24 + 16;
    }
    if (risks.length) {
      ctx.fillStyle = "#A84632";
      ctx.font = "600 14px Manrope, sans-serif";
      ctx.fillText("WATCH-OUT", 100, y);
      ctx.fillStyle = "#1A1A1A";
      ctx.font = "400 18px Manrope, sans-serif";
      const lines = wrap(ctx, risks[0], 640);
      lines.slice(0, 2).forEach((l, i) => ctx.fillText(l, 100, y + 28 + i * 24));
    }

    // Footer
    ctx.fillStyle = "#5C665D";
    ctx.font = "500 14px Manrope, sans-serif";
    ctx.fillText("What's your CFO Grade? — cfograde.com", 100, H - 100);

    setDataUrl(canvas.toDataURL("image/png"));
  }, [open, report]);

  function handleDownload() {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    const safe = (report.input?.company_name || "bankable")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
    a.download = `${safe}-cfo-grade.png`;
    a.click();
  }

  async function handleCopy() {
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("Image copied — paste it anywhere.");
    } catch {
      toast.error("Your browser blocked the copy. Use Download instead.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] transition-all duration-300"
          data-testid="share-score-btn"
        >
          <Share2 size={14} strokeWidth={1.5} className="mr-2" />
          Share score
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D8] sm:max-w-2xl" data-testid="share-score-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1F382E]">
            Share your CFO Grade
          </DialogTitle>
          <DialogDescription className="text-[#5C665D]">
            A 1200×630 image — perfect for LinkedIn, X, or a board deck.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-[#F7F5F0] rounded-lg p-2 border border-[#E5E0D8]">
          <canvas
            ref={canvasRef}
            className="w-full h-auto rounded"
            data-testid="share-score-canvas"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2">
          <Button
            onClick={handleCopy}
            variant="outline"
            className="border-[#E5E0D8] text-[#1F382E] hover:bg-[#F7F5F0]"
            data-testid="share-score-copy-btn"
          >
            <Copy size={14} strokeWidth={1.5} className="mr-2" />
            Copy image
          </Button>
          <Button
            onClick={handleDownload}
            className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0]"
            data-testid="share-score-download-btn"
          >
            <Download size={14} strokeWidth={1.5} className="mr-2" />
            Download PNG
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
